import { Type } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { AdminRouteData } from './shared/admin-route-data';
import { AdminUsersComponent } from './components/admin-users/admin-users.component';
import { AccessRenewalComponent } from './components/access-renewal/access-renewal.component';
import { UsersAccessReportComponent } from './components/users-access-report/users-access-report.component';
import { ClientComponent } from './components/client/client.component';
import { JdcManagerComponent } from './components/jdc-manager/jdc-manager.component';
import { GoldSyncComponent } from './components/gold-sync/gold-sync.component';
import { BizzHierComponent } from './components/bizz-hier/bizz-hier.component';
import { BizzHierViewComponent } from './components/bizz-hier-view/bizz-hier-view.component';
import { SystemSettingsComponent } from './components/system-settings/system-settings.component';
import { EmailSettingsComponent } from './components/email-settings/email-settings.component';
import { CacheControlComponent } from './components/cache-control/cache-control.component';
import { TemplateAssignComponent } from './components/template-assign/template-assign.component';
import { AdminFiltersComponent } from './components/admin-filters/admin-filters.component';
import { ProcessMapComponent } from './components/process-map/process-map.component';
import { MasterDataComponent } from './components/master-data/master-data.component';
import { SystemMappingComponent } from './components/master-data/system-mapping/system-mapping.component';
import { SystemMessageComponent } from './components/system-message/system-message.component';
import { NotificationComponent } from './components/notification/notification.component';
import { NotifEngineComponent } from './components/notif-engine/notif-engine.component';
import { GridBuilderComponent } from './components/grid-builder/grid-builder.component';
import { AdminResetDsConnectionComponent } from './components/admin-reset-ds-connection/admin-reset-ds-connection.component';
import { TemplatesComponent } from './components/templates/templates.component';
import { ScreensComponent } from './components/screens/screens.component';
import { RulesEngineComponent } from './components/rules-engine/rules-engine.component';
import { AdminJobConsoleListComponent } from './components/admin-job-console-list/admin-job-console-list.component';
import { ChangeLogJobRunDetailsComponent } from './components/change-log-job-run-details/change-log-job-run-details.component';
import { AuditEngineComponent } from './components/audit-engine/audit-engine.component';
import { AdminActionsComponent } from './components/admin-actions/admin-actions.component';
import { AdminDashboardsComponent } from './components/admin-dashboards/admin-dashboards.component';
import { ChartRolloverMaintenanceComponent } from './components/chart-rollover-maintenance/chart-rollover-maintenance.component';
import { PostingPartnersComponent } from './components/posting-partners/posting-partners.component';
import { AggregationTriggerComponent } from './components/aggregation-trigger/aggregation-trigger.component';
import { PdfFormOrderComponent } from './components/pdf-form-order/pdf-form-order.component';
/** Tab strip link — shared between shell tabs and router paths. */
export interface AdminTabLink {
  link: string;
  label: string;
  access: number;
  id: string;
  parentTab?: string;
}

export interface AdminRouteDefinition {
  path: string;
  breadcrumb: string;
  title: string;
  component: Type<unknown>;
  id?: string;
  access?: number;
  parentTab?: string;
  /** Router/tab URL segment(s); defaults to `path`. */
  tabLink?: string;
  showInTabBar?: boolean;
  children?: AdminRouteDefinition[];
  extraData?: Partial<AdminRouteData>;
}

function routeData(def: AdminRouteDefinition): AdminRouteData {
  return {
    breadcrumb: def.breadcrumb,
    title: def.title,
    ...def.extraData,
  };
}

function toAngularRoute(def: AdminRouteDefinition): Route {
  const route: Route = {
    path: def.path,
    component: def.component,
    data: routeData(def),
  };
  if (def.children?.length) {
    route.children = def.children.map((child) => ({
      path: child.path,
      component: child.component,
      data: routeData(child),
    }));
  }
  return route;
}

function toTabLink(def: AdminRouteDefinition): AdminTabLink {
  return {
    link: def.tabLink ?? def.path,
    label: def.breadcrumb,
    access: def.access ?? 4,
    id: def.id ?? def.path,
    parentTab: def.parentTab,
  };
}

/**
 * Single source of truth for /admin routes and tab strip.
 * Tab order matches the legacy admin shell tabset.
 */
export const ADMIN_ROUTE_DEFINITIONS: AdminRouteDefinition[] = [
  { path: 'client', breadcrumb: 'Clients', title: 'Client', component: ClientComponent, id: 'clients', access: 5, showInTabBar: true },
  { path: 'users-list', breadcrumb: 'Users', title: 'Users List', component: AdminUsersComponent, id: 'users-list', access: 3, showInTabBar: true },
  { path: 'all-users-list', breadcrumb: 'All Users', title: 'All Users List', component: AdminUsersComponent, id: 'all-users-list', access: 4, showInTabBar: true, extraData: { allUsers: true } },
  { path: 'user-expiry-report', breadcrumb: 'Access Renewal', title: 'Access Renewal', component: AccessRenewalComponent, id: 'user-expiry-report', access: 3, showInTabBar: true },
  { path: 'users-list-module', breadcrumb: 'Users Access Report', title: 'Users Access Report', component: UsersAccessReportComponent, id: 'users-access-report', access: 3, showInTabBar: true },
  { path: 'system-settings', breadcrumb: 'System Settings', title: 'System Settings', component: SystemSettingsComponent, id: 'system-settings', access: 4, showInTabBar: true },
  { path: 'jdc-manager', breadcrumb: 'Application', title: 'JDC Manager', component: JdcManagerComponent, id: 'jdc-manager', access: 4, showInTabBar: true },
  { path: 'actions-list', breadcrumb: 'Actions', title: 'Actions', component: AdminActionsComponent, id: 'actions-list', access: 4, showInTabBar: true },
  { path: 'grid-list', breadcrumb: 'Grids', title: 'Grid List', component: GridBuilderComponent, id: 'grid-list', access: 4, showInTabBar: true, extraData: { listMode: true } },
  { path: 'screen-list', breadcrumb: 'Screens', title: 'Screen List', component: ScreensComponent, id: 'screen-list', access: 4, showInTabBar: true },
  { path: 'templates-list', breadcrumb: 'Manage Templates', title: 'Templates List', component: TemplatesComponent, id: 'templates-list', access: 4, showInTabBar: true },
  { path: 'bizz-hier', breadcrumb: 'Business Hierarchy', title: 'Business Hierarchy', component: BizzHierComponent, id: 'bizz-hier', access: 4, showInTabBar: true },
  { path: 'gold-sync', breadcrumb: 'Gold Sync', title: 'Gold Sync', component: GoldSyncComponent, id: 'gold-sync', access: 4, showInTabBar: true },
  { path: 'cache-control', breadcrumb: 'Cache Control', title: 'Cache Control', component: CacheControlComponent, id: 'cache-control', access: 4, showInTabBar: true },
  { path: 'template-assign', breadcrumb: 'Template Assign', title: 'Template Assign', component: TemplateAssignComponent, id: 'template-assign', access: 3, showInTabBar: true },
  { path: 'rules-engine', breadcrumb: 'Rules Engine', title: 'Rules Engine', component: RulesEngineComponent, id: 'rules-engine', access: 4, showInTabBar: true },
  { path: 'admin-filters', breadcrumb: 'Filters', title: 'Admin Filters', component: AdminFiltersComponent, id: 'admin-filters', access: 4, showInTabBar: true },
  { path: 'audit-engine', breadcrumb: 'Audit Engine', title: 'Audit Engine', component: AuditEngineComponent, id: 'audit-engine', access: 4, showInTabBar: true },
  { path: 'admin-dashboards', breadcrumb: 'Admin Dashboards', title: 'Admin Dashboards', component: AdminDashboardsComponent, id: 'admin-dashboards', access: 4, showInTabBar: true },
  {
    path: 'master-data',
    breadcrumb: 'Master Data',
    title: 'Master Data',
    component: MasterDataComponent,
    id: 'master-data',
    access: 4,
    parentTab: 'master-data',
    tabLink: 'master-data/system-mapping',
    showInTabBar: true,
    children: [
      {
        path: 'system-mapping',
        breadcrumb: 'System To Form Mapping',
        title: 'System To Form Mapping',
        component: SystemMappingComponent,
      },
    ],
  },
  { path: 'notification', breadcrumb: 'Notification', title: 'Notification', component: NotificationComponent, id: 'notification', access: 4, showInTabBar: true },
  { path: 'system-message', breadcrumb: 'System Message', title: 'System Message', component: SystemMessageComponent, id: 'system-message', access: 4, showInTabBar: true },
  { path: 'job-console-list', breadcrumb: 'Job Console', title: 'Job Console', component: AdminJobConsoleListComponent, id: 'job-console-list', access: 4, showInTabBar: true },
  { path: 'change-log-job-run-details', breadcrumb: 'Change Log Job Run', title: 'Change Log', component: ChangeLogJobRunDetailsComponent, id: 'change-log-job-run-details', access: 4, showInTabBar: true },
  // Routed screens without a tab (legacy deep links / hidden entries)
  { path: 'bu-le', breadcrumb: 'BU/LE', title: 'BU/LE', component: BizzHierViewComponent, showInTabBar: false },
  { path: 'email-settings', breadcrumb: 'Email Settings', title: 'Email Settings', component: EmailSettingsComponent, showInTabBar: false },
  { path: 'tax-placemat', breadcrumb: 'Tax Placemat', title: 'Tax Placemat', component: ProcessMapComponent, showInTabBar: false },
  { path: 'admin-grid-builder', breadcrumb: 'Grid Builder', title: 'Grid Builder', component: GridBuilderComponent, showInTabBar: false },
  { path: 'reset-ds-connection', breadcrumb: 'Reset DS Connection', title: 'Reset DS Connection', component: AdminResetDsConnectionComponent, showInTabBar: false },
  { path: 'templates-type-list', breadcrumb: 'Templates Type List', title: 'Templates Type List', component: TemplatesComponent, showInTabBar: false, extraData: { typeList: true } },
  { path: 'notif-engine', breadcrumb: 'Notification Engine', title: 'Notification Engine', component: NotifEngineComponent, showInTabBar: false },
  { path: 'chart-rollover', breadcrumb: 'Chart Rollover', title: 'Chart Rollover', component: ChartRolloverMaintenanceComponent, showInTabBar: false },
  { path: 'posting-partners', breadcrumb: 'Posting Partners', title: 'Posting Partners', component: PostingPartnersComponent, showInTabBar: false },
  { path: 'aggregation-trigger', breadcrumb: 'Aggregation Trigger', title: 'Aggregation Trigger', component: AggregationTriggerComponent, showInTabBar: false },
  { path: 'pdf-form-order', breadcrumb: 'PDF Form Order', title: 'PDF Form Order', component: PdfFormOrderComponent, showInTabBar: false },
];

export function buildAdminTabLinks(): AdminTabLink[] {
  return ADMIN_ROUTE_DEFINITIONS.filter((d) => d.showInTabBar !== false).map(toTabLink);
}

export function buildAdminChildRoutes(): Routes {
  return [
    { path: '', redirectTo: 'users-list', pathMatch: 'full' },
    ...ADMIN_ROUTE_DEFINITIONS.map(toAngularRoute),
  ];
}
